import JobSearchAdapter from 'jobs/infra/JobSearchAdapter'
import { computed, makeObservable, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import { Subscription, filter } from 'rxjs'
import Workspace, { WorkspaceClassification } from 'iam/models/Workspace'
import JobSearchQueryStringBuilder from 'jobs/infra/JobSearchQueryStringBuilder'
import { JobScopeOption, JobStatus } from 'jobs/dtos/job'
import { JobStatusFilterOption } from 'jobs/views/JobManagement/JobStatusFilterOption'
import { DateRange } from 'infra/FilterControls/DateRange/DateRangeFilter'
import FirmDashboardContractViewModel from '../FirmDashboardContractViewModel'

/**
 * View model for managing the quick select filters.
 */
export default class FirmDashboardQuickSelectFilterViewModel {
  contractInfo = new FirmDashboardContractViewModel()

  /**
   * Current workspace to filter by.
   */
  @observable
  workspace = Workspace.none

  /**
   * Total count for under construction jobs.
   */
  @observable
  underConstructionJobsCount: number = 0

  /**
   * Total count for in progress jobs.
   */
  @observable
  inProgressJobsCount: number = 0

  /**
   * Total count for completed jobs.
   */
  @observable
  completedJobsCount: number = 0

  /**
   * Total count for info gathering jobs.
   */
  @observable
  infoGatheringJobsCount: number = 0

  /**
   * Observable subscription used for disposing on deactivate.
   */
  private subscription = new Subscription()

  /**
   * Constructor.
   */
  constructor(private jobFetcher: JobSearchAdapter) {
    makeObservable(this)
  }

  /**
   * Whether the current workspace is B2B2C.
   */
  @computed
  get isB2B2CWorkspace(): boolean {
    return this.workspace.classification === WorkspaceClassification.B2B2C
  }

  getTotalJobsFilterCount(option: JobStatusFilterOption): number {
    switch (option) {
      case JobStatusFilterOption.Onboarding:
        return this.underConstructionJobsCount
      case JobStatusFilterOption.InProgress:
        return this.inProgressJobsCount
      case JobStatusFilterOption.Closed:
        return this.completedJobsCount
      case JobStatusFilterOption.InfoGathering:
        return this.infoGatheringJobsCount
      default:
        return 0
    }
  }

  mapJobStatusOptionToString(option: JobStatusFilterOption): string {
    switch (option) {
      case JobStatusFilterOption.Onboarding:
        return 'underConstruction'
      case JobStatusFilterOption.InfoGathering:
        return 'infoGathering'
      case JobStatusFilterOption.InProgress:
        return 'inProgress'
      case JobStatusFilterOption.Closed:
        return 'completed'
      default:
        return ''
    }
  }

  /**
   * Sets the workspace filter.
   * @param workspace Workspace to filter by.
   */
  async setWorkspace(workspace: Workspace) {
    this.workspace = workspace
    this.contractInfo.setCurrentWorkspace(workspace)
    await this.fetchJobsStatusCount(workspace)
  }

  /**
   * Fetch the total count for jobs with status:
   * `UNDER_CONSTRUCTION`, `CLAIMED`, `CLOSED` and `INFO_GATHERING`
   * @param workspace
   */
  private async fetchJobsStatusCount(workspace: Workspace) {
    const getCountForStatuses = (statuses: JobStatus[]) =>
      this.jobFetcher.getTotal({
        scope: JobScopeOption.All,
        filter: new JobSearchQueryStringBuilder()
          .withWorkspaceIds([workspace.id])
          .withStatuses(statuses)
          .withinDateCreatedRange({
            from: this.contractInfo.contractStartDate,
            to: this.contractInfo.contractEndDate,
          } as DateRange)
          .build(),
        $limit: 0,
      })

    const [
      underConstructionCount,
      inProgressCount,
      completedCount,
      infoGatheringCount,
    ] = await Promise.all([
      getCountForStatuses(['UNDER_CONSTRUCTION']),
      getCountForStatuses(['CLAIMED', 'ON_HOLD', 'IDLE']),
      getCountForStatuses(['CLOSED']),
      getCountForStatuses(['INFO_GATHERING']),
    ])

    runInAction(() => {
      this.underConstructionJobsCount = underConstructionCount
      this.inProgressJobsCount = inProgressCount
      this.completedJobsCount = completedCount
      this.infoGatheringJobsCount = infoGatheringCount
    })
  }

  /**
   * Activates the view model.
   */
  activate = task.resolved(async () => {
    this.subscription = new Subscription()
    this.subscription = this.jobFetcher.events$
      .pipe(filter((event) => event.type === 'JobUpdated'))
      .subscribe(async (event) => {
        if (event.job.workspace.id === this.workspace.id) {
          await this.fetchJobsStatusCount(this.workspace)
        }
      })
  })

  /**
   * Called when the view model is not being actively used.
   */
  deactivate() {
    this.subscription.unsubscribe()
  }
}
